"use client"

import { useState, useEffect } from "react"
import {
  Plus,
  Trash2,
  FileText,
  Shapes,
  Eye,
  RefreshCcw,
  Download,
  ArrowLeft,
  Sparkles,
  X,
  Send,
  Clock,
  ChevronDown,
  ChevronUp,
  Check,
  Upload,
  ImageIcon,
  ImageIcon as ImageIconLucide,
} from "lucide-react"
import { generatePresignedUrl } from "../postFileAws"
import { postAws } from "../postAws"
import { postTeacherLatexCompileFile, postTeacherImageAskAiLatex, postTeacherImagePdfImage } from "../postFileAws"

const RenderingLatexAndPdfOfDigitisedNotes = ({ noteData, onClose }) => {
  const [pages, setPages] = useState([])
  const [currentPageId, setCurrentPageId] = useState(null)
  const [activeEditor, setActiveEditor] = useState("text")
  const [pdfUrl, setPdfUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isCompiling, setIsCompiling] = useState(false)
  const [compilationStatus, setCompilationStatus] = useState(null)
  const [editedBlocks, setEditedBlocks] = useState(new Set())

  // Versions state
  const [versions, setVersions] = useState([])
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [showVersions, setShowVersions] = useState(false)
  const [isLoadingVersions, setIsLoadingVersions] = useState(false)

  // AI Modal states
  const [showAIModal, setShowAIModal] = useState(false)
  const [aiQuery, setAiQuery] = useState("")
  const [isAIProcessing, setIsAIProcessing] = useState(false)
  const [currentAIBlock, setCurrentAIBlock] = useState(null)
  const [aiMessages, setAiMessages] = useState({}) // Store AI messages for each block

  // Image upload states for AI
  const [selectedImage, setSelectedImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [isUploadingImage, setIsUploadingImage] = useState(false)

  // Image upload states for text blocks
  const [showImageModal, setShowImageModal] = useState(false)
  const [currentImageBlock, setCurrentImageBlock] = useState(null)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [imageFileName, setImageFileName] = useState("")
  const [imageCaption, setImageCaption] = useState("")
  const [isUploadingBlockImage, setIsUploadingBlockImage] = useState(false)
  const [blockImages, setBlockImages] = useState({}) // Store images per block

  const currentPage = pages.find((p) => p.id === currentPageId) || pages[0]

  // Fixed function to properly parse LaTeX and separate diagrams from text
  const parsePageContent = (pageLatexCode) => {
    const blocks = []
    let order = 0
    const cleanCode = pageLatexCode.trim()

    if (!cleanCode) return blocks

    // Step 1: Find all TikZ diagrams first
    const tikzRegex = /\\begin\{tikzpicture\}[\s\S]*?\\end\{tikzpicture\}/g
    const tikzMatches = []
    let match

    while ((match = tikzRegex.exec(cleanCode)) !== null) {
      tikzMatches.push({
        content: match[0],
        start: match.index,
        end: match.index + match[0].length,
      })
    }

    // Step 2: Create diagram blocks for TikZ diagrams
    tikzMatches.forEach((tikz) => {
      blocks.push({
        id: `block_${Date.now()}_${Math.random()}_${order}`,
        type: "diagram",
        content: tikz.content.trim(),
        order: order++,
      })
    })

    // Step 3: Remove TikZ diagrams from the content to get text-only content
    let textOnlyContent = cleanCode
    // Remove tikz diagrams in reverse order to maintain indices
    for (let i = tikzMatches.length - 1; i >= 0; i--) {
      const tikz = tikzMatches[i]
      textOnlyContent = textOnlyContent.slice(0, tikz.start) + textOnlyContent.slice(tikz.end)
    }

    // Step 4: Process text content and create text blocks
    if (textOnlyContent.trim()) {
      // Split text content into logical blocks
      const textBlocks = splitTextIntoBlocks(textOnlyContent.trim())

      textBlocks.forEach((textBlock) => {
        if (textBlock.trim()) {
          blocks.push({
            id: `block_${Date.now()}_${Math.random()}_${order}`,
            type: "text",
            content: textBlock.trim(),
            order: order++,
          })
        }
      })
    }

    // Sort blocks by their original order in the document
    return blocks.sort((a, b) => a.order - b.order)
  }

  // Helper function to split text content into logical blocks
  const splitTextIntoBlocks = (textContent) => {
    const blocks = []

    // Split by major section breaks
    const sectionBreaks = [/\\section\{[^}]*\}/g, /\\subsection\{[^}]*\}/g, /\\subsubsection\{[^}]*\}/g, /\\newpage/g]

    // Find all section positions
    const breakPositions = []
    sectionBreaks.forEach((regex) => {
      let match
      while ((match = regex.exec(textContent)) !== null) {
        breakPositions.push({
          position: match.index,
          type: "section",
        })
      }
    })

    // Sort break positions
    breakPositions.sort((a, b) => a.position - b.position)

    // Split content at break positions
    let lastPosition = 0
    breakPositions.forEach((breakPos) => {
      if (breakPos.position > lastPosition) {
        const blockContent = textContent.slice(lastPosition, breakPos.position).trim()
        if (blockContent) {
          blocks.push(blockContent)
        }
      }
      lastPosition = breakPos.position
    })

    // Add remaining content
    if (lastPosition < textContent.length) {
      const remainingContent = textContent.slice(lastPosition).trim()
      if (remainingContent) {
        blocks.push(remainingContent)
      }
    }

    // If no breaks found, treat as single block but check for environment boundaries
    if (blocks.length === 0) {
      blocks.push(textContent)
    }

    // Post-process blocks to ensure environment integrity
    return blocks.map((block) => ensureEnvironmentIntegrity(block))
  }

  // Helper function to ensure environments are complete in text blocks
  const ensureEnvironmentIntegrity = (content) => {
    // Find all \begin{} and \end{} pairs
    const beginMatches = [...content.matchAll(/\\begin\{([^}]+)\}/g)]
    const endMatches = [...content.matchAll(/\\end\{([^}]+)\}/g)]

    // Count environments
    const envCounts = {}
    beginMatches.forEach((match) => {
      const env = match[1]
      envCounts[env] = (envCounts[env] || 0) + 1
    })

    endMatches.forEach((match) => {
      const env = match[1]
      envCounts[env] = (envCounts[env] || 0) - 1
    })

    // Check if all environments are balanced
    const unbalanced = Object.entries(envCounts).filter(([env, count]) => count !== 0)

    if (unbalanced.length > 0) {
      console.warn("Unbalanced environments detected:", unbalanced)
      // For now, return content as-is, but this could be enhanced
      // to try to fix unbalanced environments
    }

    return content
  }

  const parseLatexIntoPages = (latexCode) => {
    const cleanCode = latexCode
      .replace(/\\documentclass\{[^}]*\}/g, "")
      .replace(/\\usepackage(?:\[[^\]]*\])?\{[^}]*\}/g, "")
      .replace(/\\begin\{document\}/g, "")
      .replace(/\\end\{document\}/g, "")
      .replace(/\\title\{[^}]*\}/g, "")
      .replace(/\\author\{[^}]*\}/g, "")
      .replace(/\\date\{[^}]*\}/g, "")
      .replace(/\\maketitle/g, "")
      .trim()

    // Split by \newpage - this is where we handle page breaks
    const pageContents = cleanCode.split(/\\newpage\s*/).filter((content) => content.trim())

    console.log("Found", pageContents.length, "pages after splitting by \\newpage")

    const parsedPages = pageContents.map((pageContent, index) => {
      const blocks = parsePageContent(pageContent)
      console.log(
        `Page ${index + 1} blocks:`,
        blocks.map((b) => ({ type: b.type, contentPreview: b.content.substring(0, 50) + "..." })),
      )
      return {
        id: `page_${Date.now()}_${index}`,
        title: `Page ${index + 1}`,
        blocks: blocks,
      }
    })

    if (parsedPages.length === 0 && cleanCode.trim()) {
      const blocks = parsePageContent(cleanCode)
      console.log(
        "Single page blocks:",
        blocks.map((b) => ({ type: b.type, contentPreview: b.content.substring(0, 50) + "..." })),
      )
      return [
        {
          id: `page_${Date.now()}_0`,
          title: "Page 1",
          blocks: blocks,
        },
      ]
    }

    return parsedPages
  }

  // Check if required packages exist in LaTeX content
  const checkAndAddRequiredPackages = () => {
    const requiredPackages = ["graphicx", "caption", "float"]
    const fullLatex = pages.map((page) => page.blocks.map((block) => block.content).join("\n\n")).join("\n\\newpage\n")
    const missingPackages = requiredPackages.filter(
      (pkg) => !fullLatex.includes(`\\usepackage{${pkg}}`) && !fullLatex.includes(`\\usepackage[`), // Check for packages with options
    )
    return missingPackages
  }

  // Get all uploaded images for recompile
  const getAllUploadedImages = () => {
    const allImages = []
    Object.values(blockImages).forEach((images) => {
      if (Array.isArray(images)) {
        images.forEach((img) => {
          allImages.push({
            key: img.fileName,
            s3uri: img.s3Uri,
          })
        })
      }
    })
    return allImages
  }

  // Fetch versions
  const fetchVersions = async () => {
    setIsLoadingVersions(true)
    try {
      const payload = {
        request: "get-digital-notes",
        body: {
          teacherId: localStorage.getItem("teacherId"),
          classroomId: localStorage.getItem("classRoomId"),
        },
      }
      const response = await postAws("https://api-dev.aurait.ai/DigitiseNotesFromPdf/getNotes", "POST", payload)
      if (response.statusCode === 200) {
        const responseBody = typeof response.body === "string" ? JSON.parse(response.body) : response.body
        const currentNote = responseBody.notes?.find((note) => note._id === noteData._id)
        if (currentNote && currentNote.latexPdfVersions) {
          // Sort versions by createdAt in descending order (newest first)
          const sortedVersions = currentNote.latexPdfVersions.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          )
          setVersions(sortedVersions)
        }
      }
    } catch (error) {
      console.error("Error fetching versions:", error)
    } finally {
      setIsLoadingVersions(false)
    }
  }

  // Load version content
  const loadVersion = async (version) => {
    try {
      setSelectedVersion(version)
      // Load LaTeX content
      if (version.latexS3Uri) {
        await loadLatexContent(version.latexS3Uri)
      }
      // Load PDF content
      if (version.pdfS3Uri) {
        await loadPdfContent(version.pdfS3Uri)
      }
      // Reset unsaved changes since we're loading a version
      setHasUnsavedChanges(false)
      setEditedBlocks(new Set())
    } catch (error) {
      console.error("Error loading version:", error)
    }
  }

  // Load current version (latest) content
  const loadCurrentVersion = async () => {
    try {
      setSelectedVersion(null) // Clear selected version to show we're on current
      // Load current LaTeX content
      if (noteData.processingResult && noteData.processingResult.latexSourceUri) {
        await loadLatexContent(noteData.processingResult.latexSourceUri)
      }
      // Load current PDF content
      if (noteData.processingResult && noteData.processingResult.digitisedPdfUri) {
        await loadPdfContent(noteData.processingResult.digitisedPdfUri)
      }
      // Reset unsaved changes since we're loading the current version
      setHasUnsavedChanges(false)
      setEditedBlocks(new Set())
    } catch (error) {
      console.error("Error loading current version:", error)
    }
  }

  useEffect(() => {
    if (noteData) {
      initializeNoteContent()
      fetchVersions()
    }
  }, [noteData])

  const initializeNoteContent = async () => {
    setIsLoading(true)
    try {
      if (noteData.processingResult && noteData.processingResult.latexSourceUri) {
        await loadLatexContent(noteData.processingResult.latexSourceUri)
      }
      if (noteData.processingResult && noteData.processingResult.digitisedPdfUri) {
        await loadPdfContent(noteData.processingResult.digitisedPdfUri)
      }
    } catch (error) {
      console.error("Error initializing note content:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadLatexContent = async (latexUri) => {
    try {
      let s3Uri = latexUri.replace("s3://padhai-aurait-tech/", "")
      if (s3Uri.includes("eachers/")) {
        s3Uri = s3Uri.replace("eachers/", "Teachers/")
      }
      const presignedUrl = await generatePresignedUrl(s3Uri)
      console.log("latex ka presigned url", presignedUrl)
      if (presignedUrl) {
        const response = await fetch(presignedUrl)
        if (response.ok) {
          const latexContent = await response.text()
          const parsedPages = parseLatexIntoPages(latexContent)
          if (parsedPages.length > 0) {
            setPages(parsedPages)
            setCurrentPageId(parsedPages[0].id)
          }
        }
      }
    } catch (error) {
      console.error("Error fetching LaTeX content:", error)
    }
  }

  const loadPdfContent = async (pdfUri) => {
    try {
      let s3Uri = pdfUri.replace("s3://padhai-aurait-tech/", "")
      if (s3Uri.includes("eachers/")) {
        s3Uri = s3Uri.replace("eachers/", "Teachers/")
      }
      const presignedUrl = await generatePresignedUrl(s3Uri)
      if (presignedUrl) {
        setPdfUrl(presignedUrl)
      }
    } catch (error) {
      console.error("Error fetching PDF content:", error)
    }
  }

  const addNewPage = () => {
    const newId = Date.now().toString()
    const newPage = {
      id: newId,
      title: `Page ${pages.length + 1}`,
      blocks: [],
    }
    setPages([...pages, newPage])
    setCurrentPageId(newId)
    setHasUnsavedChanges(true)
  }

  const addNewBlock = (type) => {
    const newBlock = {
      id: `block_${Date.now()}_${currentPage.blocks.length}`,
      type,
      content:
        type === "text"
          ? "\\section{New Section}\nAdd your content here..."
          : "\\begin{tikzpicture}\n% Add your diagram here\n\\draw (0,0) -- (2,2);\n\\end{tikzpicture}",
      order: currentPage.blocks.length,
    }
    const updatedPage = {
      ...currentPage,
      blocks: [...currentPage.blocks, newBlock],
    }
    setPages(pages.map((page) => (page.id === currentPageId ? updatedPage : page)))
    setHasUnsavedChanges(true)
  }

  const updateBlockContent = (blockId, content) => {
    const updatedBlocks = currentPage.blocks.map((block) => (block.id === blockId ? { ...block, content } : block))
    const updatedPage = { ...currentPage, blocks: updatedBlocks }
    setPages(pages.map((page) => (page.id === currentPageId ? updatedPage : page)))
    setHasUnsavedChanges(true)
    // Track this block as edited
    setEditedBlocks((prev) => new Set([...prev, blockId]))
  }

  const deleteBlock = (blockId) => {
    const updatedBlocks = currentPage.blocks.filter((block) => block.id !== blockId)
    const updatedPage = { ...currentPage, blocks: updatedBlocks }
    setPages(pages.map((page) => (page.id === currentPageId ? updatedPage : page)))
    setHasUnsavedChanges(true)
    // Remove AI message for this block if it exists
    if (aiMessages[blockId]) {
      const newAiMessages = { ...aiMessages }
      delete newAiMessages[blockId]
      setAiMessages(newAiMessages)
    }
    // Remove block images if they exist
    if (blockImages[blockId]) {
      const newBlockImages = { ...blockImages }
      delete newBlockImages[blockId]
      setBlockImages(newBlockImages)
    }
  }

  const getCurrentTypeBlocks = () => {
    return currentPage?.blocks?.filter((block) => block.type === activeEditor) || []
  }

  const getTotalBlocksCount = (type) => {
    return pages.reduce((total, page) => {
      return total + page.blocks.filter((block) => block.type === type).length
    }, 0)
  }

  const handleAskAI = (block) => {
    setCurrentAIBlock(block)
    setAiQuery("")
    setSelectedImage(null)
    setImagePreview(null)
    setShowAIModal(true)
  }

  // Handle Add Image for text blocks
  const handleAddImage = (block) => {
    setCurrentImageBlock(block)
    setSelectedImageFile(null)
    setImageFileName("")
    setImageCaption("")
    setShowImageModal(true)
  }

  // Handle image file selection for text blocks
  const handleImageFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Validate file type (only JPG and PNG)
      if (!file.type.match(/^image\/(jpeg|jpg|png)$/i)) {
        alert("Please select only JPG or PNG files")
        return
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert("Image size should be less than 10MB")
        return
      }
      setSelectedImageFile(file)
      setImageFileName(file.name)
    }
  }

  // Upload image for text block
  const handleUploadBlockImage = async () => {
    if (!selectedImageFile || !imageFileName.trim() || !currentImageBlock) return
    setIsUploadingBlockImage(true)
    try {
      const teacherId = localStorage.getItem("teacherId")
      const noteTitle = noteData.noteTitle || "document"
      const fileName = imageFileName.trim()
      const uploadResult = await postTeacherImagePdfImage(selectedImageFile, teacherId, noteTitle, fileName)
      if (uploadResult && uploadResult.uri) {
        let s3Uri = uploadResult.uri
        // Handle /eachers/ to /Teachers/ conversion
        if (s3Uri.includes("/eachers/")) {
          s3Uri = s3Uri.replace("/eachers/", "/Teachers/")
        }
        // Store image info
        const imageInfo = {
          fileName: fileName,
          s3Uri: s3Uri,
          caption: imageCaption.trim() || "this is image",
        }
        // Add to block images
        setBlockImages((prev) => ({
          ...prev,
          [currentImageBlock.id]: [...(prev[currentImageBlock.id] || []), imageInfo],
        }))
        // Generate LaTeX figure code
        const figureCode = `\n\n\\begin{figure}[H]\n    \\centering\n    \\includegraphics[width=0.7\\textwidth]{${fileName}}\n    \\caption{${imageInfo.caption}}\n\\end{figure}\n\n`
        // Add figure code to the end of the text block
        const updatedContent = currentImageBlock.content + figureCode
        updateBlockContent(currentImageBlock.id, updatedContent)
        // Close modal and reset states
        setShowImageModal(false)
        setCurrentImageBlock(null)
        setSelectedImageFile(null)
        setImageFileName("")
        setImageCaption("")
        // Show success message
        const successMessage = document.createElement("div")
        successMessage.className = "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
        successMessage.textContent = "Image uploaded and added successfully!"
        document.body.appendChild(successMessage)
        setTimeout(() => {
          if (document.body.contains(successMessage)) {
            document.body.removeChild(successMessage)
          }
        }, 3000)
      } else {
        throw new Error("Failed to upload image")
      }
    } catch (error) {
      console.error("Error uploading image:", error)
      // Show error message
      const errorMessage = document.createElement("div")
      errorMessage.className = "fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
      errorMessage.textContent = error.message || "Failed to upload image. Please try again."
      document.body.appendChild(errorMessage)
      setTimeout(() => {
        if (document.body.contains(errorMessage)) {
          document.body.removeChild(errorMessage)
        }
      }, 3000)
    } finally {
      setIsUploadingBlockImage(false)
    }
  }

  const getFullPageLatex = () => {
    if (!currentPage) return ""
    return currentPage.blocks.map((block) => block.content).join("\n\n")
  }

  // Handle image selection for AI
  const handleImageSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file")
        return
      }
      // Validate file size (e.g., max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert("Image size should be less than 10MB")
        return
      }
      setSelectedImage(file)
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  // Remove selected image for AI
  const removeSelectedImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
  }

  const handleAISend = async () => {
    if (!aiQuery.trim() || !currentAIBlock) return
    setIsAIProcessing(true)
    try {
      const fullPageLatex = getFullPageLatex()
      const blockLatex = currentAIBlock.content
      let imageS3Uri = null
      // Upload image first if selected
      if (selectedImage) {
        setIsUploadingImage(true)
        try {
          const teacherId = localStorage.getItem("teacherId")
          const noteTitle = noteData.noteTitle || "document"
          const fileName = `ai-query-image-${Date.now()}-${selectedImage.name}`
          const uploadResult = await postTeacherImageAskAiLatex(selectedImage, teacherId, noteTitle, fileName)
          if (uploadResult && uploadResult.uri) {
            imageS3Uri = uploadResult.uri
            // Handle /eachers/ to /Teachers/ conversion
            if (imageS3Uri.includes("/eachers/")) {
              imageS3Uri = imageS3Uri.replace("eachers/", "Teachers/")
            }
          } else {
            throw new Error("Failed to upload image")
          }
        } catch (uploadError) {
          console.error("Error uploading image:", uploadError)
          throw new Error("Failed to upload image. Please try again.")
        } finally {
          setIsUploadingImage(false)
        }
      }
      const payload = {
        action: "create-with-ai",
        fullPageLatex: fullPageLatex,
        [currentAIBlock.type === "text" ? "textBlockLatex" : "diagramBlockLatex"]: blockLatex,
        userQuery: aiQuery,
        noteId: noteData._id,
        ...(imageS3Uri && { s3uri: imageS3Uri }), // Include S3 URI if image was uploaded
      }
      const response = await postAws("https://api-dev.aurait.ai/DigitiseNotesFromPdf/getNotes", "POST", payload)
      if (response.statusCode === 200) {
        const responseBody = typeof response.body === "string" ? JSON.parse(response.body) : response.body
        // Update the block content with AI response
        updateBlockContent(currentAIBlock.id, responseBody.latex)
        // Store AI message for this block
        setAiMessages((prev) => ({
          ...prev,
          [currentAIBlock.id]: responseBody.message,
        }))
        // Close modal and reset states
        setShowAIModal(false)
        setAiQuery("")
        setCurrentAIBlock(null)
        setSelectedImage(null)
        setImagePreview(null)
        // Show success message
        const successMessage = document.createElement("div")
        successMessage.className = "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
        successMessage.textContent = "AI has processed your request successfully!"
        document.body.appendChild(successMessage)
        setTimeout(() => {
          if (document.body.contains(successMessage)) {
            document.body.removeChild(successMessage)
          }
        }, 3000)
      } else {
        throw new Error("Failed to process AI request")
      }
    } catch (error) {
      console.error("Error processing AI request:", error)
      // Show error message
      const errorMessage = document.createElement("div")
      errorMessage.className = "fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
      errorMessage.textContent = error.message || "Failed to process AI request. Please try again."
      document.body.appendChild(errorMessage)
      setTimeout(() => {
        if (document.body.contains(errorMessage)) {
          document.body.removeChild(errorMessage)
        }
      }, 3000)
    } finally {
      setIsAIProcessing(false)
      setIsUploadingImage(false)
    }
  }

  const handleRecompilePdf = async () => {
    setIsCompiling(true)
    setCompilationStatus(null)
    try {
      // Get required data from localStorage
      const teacherId = localStorage.getItem("teacherId")
      const classroomId = localStorage.getItem("classRoomId")
      if (!teacherId || !classroomId) {
        throw new Error("Missing required user data. Please refresh and try again.")
      }
      // Check for missing packages
      const missingPackages = checkAndAddRequiredPackages()
      // Generate complete LaTeX document preserving original structure
      const generateCompleteLatex = () => {
        let header = `\\documentclass[11pt]{article}\\usepackage{amsmath,caption,float,amsfonts,amssymb,graphicx,geometry,tikz,physics}\\usepackage[utf8]{inputenc}\\usetikzlibrary{arrows,shapes,positioning,circuits.ee.IEC}\\geometry{margin=0.8in}\\setlength{\\parindent}{0pt}\\setlength{\\parskip}{0.5em}`
        // Add missing packages
        missingPackages.forEach((pkg) => {
          header += `\\usepackage{${pkg}}`
        })
        header += `\\title{${noteData.noteTitle || "digitiseTestingg"}}\\author{}\\date{}\\begin{document}\\maketitle`
        const footer = `\\end{document}`
        const cleanLatexContent = (content) => {
          return content
            .replace(/```latex/g, "")
            .replace(/```/g, "")
            .replace(/\\documentclass(\[[^\]]*\])?\{[^}]+\}/g, "")
            .replace(/\\usepackage(\[[^\]]*\])?\{[^}]+\}/g, "")
            .replace(/\\usetikzlibrary\{[^}]+\}/g, "")
            .replace(/\\geometry\{[^}]+\}/g, "")
            .replace(/\\title\{[^}]*\}/g, "")
            .replace(/\\author\{[^}]*\}/g, "")
            .replace(/\\date\{[^}]*\}/g, "")
            .replace(/\\maketitle/g, "")
            .replace(/\\begin\{document\}/g, "")
            .replace(/\\end\{document\}/g, "")
            .trim()
        }
        const pagesContent = pages
          .map((page) => {
            const pageContent = page.blocks.map((block) => cleanLatexContent(block.content)).join("\n\n")
            return pageContent
          })
          .join("\n\\newpage\n")
        return `${header}${pagesContent}${footer}`
      }
      const completeLatex = generateCompleteLatex()
      // Create a blob from the updated LaTeX content
      const latexBlob = new Blob([completeLatex], { type: "text/plain" })
      const latexFile = new File([latexBlob], `${noteData.noteTitle || "document"}-${Date.now()}.tex`, {
        type: "text/plain",
      })
      // Upload LaTeX file to S3
      const uploadResult = await postTeacherLatexCompileFile(
        latexFile,
        teacherId,
        noteData.noteTitle || "document",
        `${noteData.noteTitle || "document"}-${Date.now()}.tex`,
      )
      if (!uploadResult || !uploadResult.uri) {
        throw new Error("Failed to upload LaTeX file to storage")
      }
      // Get all uploaded images
      const allImages = getAllUploadedImages()
      // Prepare payload for PDF compilation API with images
      const payload = {
        s3Uri: uploadResult.uri,
        images: allImages, // Include images array
        fileName: `${noteData.noteTitle || "document"}-${Date.now()}.pdf`,
        noteId: noteData._id,
        noteTitle: noteData.noteTitle || "document",
        teacherId,
        classroomId,
      }
      // Call PDF compilation API
      const result = await postAws("https://api-dev.aurait.ai/compileLatexToPdf", "POST", payload)
      if (result.statusCode === 200) {
        const responseBody = typeof result.body === "string" ? JSON.parse(result.body) : result.body
        if (responseBody.success) {
          // Step 3: Create version after successful compilation
          const createVersionPayload = {
            request: "create-versions",
            body: {
              noteId: noteData._id,
              latexS3Uri: uploadResult.uri,
              pdfS3Uri: responseBody.digitisedPdfS3Uri, // Use digitisedPdfS3Uri from recompile response
            },
          }
          const versionResponse = await postAws(
            "https://api-dev.aurait.ai/DigitiseNotesFromPdf/getNotes",
            "POST",
            createVersionPayload,
          )
          if (versionResponse.statusCode === 200) {
            // Refresh versions list
            await fetchVersions()
          }
          setCompilationStatus("success")
          // Update PDF URL with the new compiled PDF
          if (responseBody.presignedUrl) {
            setPdfUrl(responseBody.presignedUrl)
          }
          // Clear edited blocks since we've successfully compiled
          setEditedBlocks(new Set())
          setHasUnsavedChanges(false)
        } else {
          throw new Error(responseBody.message || "PDF compilation failed")
        }
      } else {
        throw new Error(result.message || "Failed to compile PDF")
      }
    } catch (error) {
      console.error("Error compiling PDF:", error)
      setCompilationStatus("error")
    } finally {
      setIsCompiling(false)
    }
  }

  const downloadPDF = () => {
    if (pdfUrl) {
      const a = document.createElement("a")
      a.href = pdfUrl
      a.download = `${noteData?.noteTitle || "document"}.pdf`
      a.click()
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date)
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Loading digitised note</h3>
          <p className="text-gray-600">Please wait while we fetch your note content...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col">
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Pages with breadcrumb and versions */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          {/* Breadcrumb */}
          <div className="p-4 border-b border-gray-100">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-4"
            >
              <ArrowLeft size={16} />
              <span className="text-sm font-medium">Back to Digitised Notes</span>
            </button>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Pages</h2>
              <button
                onClick={addNewPage}
                className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
                title="Add New Page"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
          {/* Pages List */}
          <div className="flex-1 overflow-y-auto">
            {pages.map((page) => (
              <div
                key={page.id}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                  currentPageId === page.id ? "bg-blue-50 border-r-4 border-blue-600" : ""
                }`}
                onClick={() => setCurrentPageId(page.id)}
              >
                <div className="font-medium text-gray-900 mb-1">{page.title}</div>
                <div className="text-xs text-gray-500">
                  {page.blocks.filter((b) => b.type === "text").length} text,{" "}
                  {page.blocks.filter((b) => b.type === "diagram").length} diagram blocks
                </div>
              </div>
            ))}
          </div>
          {/* Previous Versions Section */}
          <div className="border-t border-gray-200">
            <button
              onClick={() => setShowVersions(!showVersions)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-gray-500" />
                <span className="font-medium text-gray-900">Previous Versions</span>
                {versions.length > 0 && (
                  <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">{versions.length}</span>
                )}
              </div>
              {showVersions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {showVersions && (
              <div className="max-h-48 overflow-y-auto border-t border-gray-100">
                {/* Current Version Button */}
                <button
                  onClick={loadCurrentVersion}
                  className={`w-full p-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 ${
                    selectedVersion === null ? "bg-blue-50" : ""
                  }`}
                >
                  <div className="text-xs font-medium text-gray-900 mb-1">Current Version</div>
                  <div className="text-xs text-gray-500">Latest changes</div>
                </button>
                {isLoadingVersions ? (
                  <div className="p-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-xs text-gray-500">Loading versions...</p>
                  </div>
                ) : versions.length === 0 ? (
                  <div className="p-4 text-center">
                    <p className="text-xs text-gray-500">No previous versions</p>
                  </div>
                ) : (
                  versions.map((version, index) => (
                    <button
                      key={version._id || index}
                      onClick={() => loadVersion(version)}
                      className={`w-full p-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0 ${
                        selectedVersion?._id === version._id ? "bg-blue-50" : ""
                      }`}
                    >
                      <div className="text-xs font-medium text-gray-900 mb-1">Version {versions.length - index}</div>
                      <div className="text-xs text-gray-500">Created: {formatDate(version.createdAt)}</div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Editor Type Tabs with action buttons */}
          <div className="bg-white border-b border-gray-200 px-6 py-3 relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setActiveEditor("text")}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium ${
                    activeEditor === "text"
                      ? "bg-blue-100 text-blue-700 border border-blue-200"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <FileText size={16} />
                  Text Blocks ({getTotalBlocksCount("text")})
                </button>
                <button
                  onClick={() => setActiveEditor("diagram")}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium ${
                    activeEditor === "diagram"
                      ? "bg-blue-100 text-blue-700 border border-blue-200"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Shapes size={16} />
                  Diagram Blocks ({getTotalBlocksCount("diagram")})
                </button>
              </div>
              <div className="flex items-center gap-3">
                {/* Show Recompile button when there are unsaved changes */}
                {hasUnsavedChanges && (
                  <button
                    onClick={handleRecompilePdf}
                    disabled={isCompiling}
                    className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                  >
                    {isCompiling ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Compiling...
                      </>
                    ) : (
                      <>
                        <RefreshCcw size={16} />
                        Recompile PDF
                      </>
                    )}
                  </button>
                )}
                {pdfUrl && (
                  <button
                    onClick={downloadPDF}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                  >
                    <Download size={16} />
                    Download PDF
                  </button>
                )}
                <button
                  onClick={() => addNewBlock(activeEditor)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                >
                  <Plus size={16} />
                  Add {activeEditor === "text" ? "Text" : "Diagram"} Block
                </button>
              </div>
            </div>
            {/* Small Success Card Overlay */}
            {compilationStatus === "success" && (
              <div className="absolute top-16 right-6 bg-green-500 text-white px-3 py-2 rounded-lg shadow-lg z-10 flex items-center gap-2 text-sm">
                <Check size={14} />
                <span>PDF compiled successfully!</span>
                <button onClick={() => setCompilationStatus(null)} className="text-white/80 hover:text-white ml-1">
                  <X size={14} />
                </button>
              </div>
            )}
            {/* Small Error Card Overlay */}
            {compilationStatus === "error" && (
              <div className="absolute top-16 right-6 bg-red-500 text-white px-3 py-2 rounded-lg shadow-lg z-10 flex items-center gap-2 text-sm">
                <X size={14} />
                <span>Compilation failed</span>
                <button onClick={() => setCompilationStatus(null)} className="text-white/80 hover:text-white ml-1">
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-1 overflow-hidden">
            {/* Editor Panel */}
            <div className="flex-1 flex flex-col bg-white">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {activeEditor === "text" ? "Text Blocks" : "Diagram Blocks"}
                  </h3>
                  <span className="text-sm text-gray-500">{currentPage?.title}</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {getCurrentTypeBlocks().map((block, index) => (
                  <div key={block.id} className="border border-gray-200 rounded-lg bg-white shadow-sm">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                      <span className="text-sm font-medium text-gray-700">
                        {activeEditor === "text" ? "Text" : "Diagram"} Block {index + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        {/* Add Image button only for text blocks */}
                        {activeEditor === "text" && (
                          <button
                            onClick={() => handleAddImage(block)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1"
                          >
                            <ImageIconLucide size={12} />
                            Add Image
                          </button>
                        )}
                        <button
                          onClick={() => handleAskAI(block)}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1"
                        >
                          <Sparkles size={12} />
                          Ask AI
                        </button>
                        <button
                          onClick={() => deleteBlock(block.id)}
                          className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                          title="Delete Block"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    {/* AI Message Display */}
                    {aiMessages[block.id] && (
                      <div className="px-4 py-3 bg-purple-50 border-b border-purple-100">
                        <div className="flex items-start gap-2">
                          <Sparkles className="text-purple-600 flex-shrink-0 mt-0.5" size={16} />
                          <div>
                            <p className="text-sm font-medium text-purple-800 mb-1">AI Assistant</p>
                            <p className="text-sm text-purple-700">{aiMessages[block.id]}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="p-4">
                      <textarea
                        value={block.content}
                        onChange={(e) => updateBlockContent(block.id, e.target.value)}
                        className="w-full h-40 border border-gray-200 rounded-lg p-3 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={
                          activeEditor === "text" ? "Enter LaTeX text content..." : "Enter TikZ diagram code..."
                        }
                      />
                    </div>
                  </div>
                ))}
                {getCurrentTypeBlocks().length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <div className="mb-4">
                      {activeEditor === "text" ? (
                        <FileText size={48} className="mx-auto text-gray-300" />
                      ) : (
                        <Shapes size={48} className="mx-auto text-gray-300" />
                      )}
                    </div>
                    <p className="text-lg font-medium mb-2">No {activeEditor} blocks yet</p>
                    <p className="text-sm">
                      Click "Add {activeEditor === "text" ? "Text" : "Diagram"} Block" to create one.
                    </p>
                  </div>
                )}
              </div>
            </div>
            {/* PDF Preview Panel */}
            <div className="w-1/2 bg-white border-l border-gray-200 flex flex-col">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Eye size={18} className="text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">PDF Preview</h3>
                  {selectedVersion ? (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      Version {versions.findIndex((v) => v._id === selectedVersion._id) + 1}
                    </span>
                  ) : (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Current Version</span>
                  )}
                </div>
              </div>
              <div className="flex-1 p-6">
                {pdfUrl ? (
                  <div className="h-full border border-gray-200 rounded-lg overflow-hidden">
                    <iframe
                      src={pdfUrl}
                      className="w-full h-full border-0"
                      title="PDF Preview"
                      style={{ minHeight: "600px" }}
                    />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg">
                    <div className="text-center">
                      <Eye className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium text-gray-600 mb-2">No PDF Preview Available</p>
                      <p className="text-sm text-gray-500">PDF content is being processed</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Image Upload Modal for Text Blocks */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <ImageIconLucide className="mr-2" size={20} />
                  Add Image to Text Block
                </h2>
                <button
                  onClick={() => {
                    setShowImageModal(false)
                    setCurrentImageBlock(null)
                    setSelectedImageFile(null)
                    setImageFileName("")
                    setImageCaption("")
                  }}
                  className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 flex-1 flex flex-col overflow-y-auto">
              {/* File Upload Section */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Image File</label>
                {!selectedImageFile ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleImageFileSelect}
                      className="hidden"
                      id="block-image-upload"
                    />
                    <label htmlFor="block-image-upload" className="cursor-pointer flex flex-col items-center">
                      <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">Click to upload an image</span>
                      <span className="text-xs text-gray-500 mt-1">Only JPG and PNG files up to 10MB</span>
                    </label>
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <ImageIcon className="w-8 h-8 text-blue-600" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{selectedImageFile.name}</p>
                        <p className="text-xs text-gray-500">{(selectedImageFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedImageFile(null)
                          setImageFileName("")
                        }}
                        className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                        title="Remove image"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {/* File Name Field */}
              {selectedImageFile && (
                <div className="mb-4">
                  <label htmlFor="image-filename" className="block text-sm font-medium text-gray-700 mb-2">
                    File Name
                  </label>
                  <input
                    type="text"
                    id="image-filename"
                    value={imageFileName}
                    onChange={(e) => setImageFileName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter file name..."
                  />
                </div>
              )}
              {/* Caption Field */}
              {selectedImageFile && (
                <div className="mb-4">
                  <label htmlFor="image-caption" className="block text-sm font-medium text-gray-700 mb-2">
                    Caption (Optional)
                  </label>
                  <input
                    type="text"
                    id="image-caption"
                    value={imageCaption}
                    onChange={(e) => setImageCaption(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter image caption..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for default caption: "this is image"</p>
                </div>
              )}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowImageModal(false)
                    setCurrentImageBlock(null)
                    setSelectedImageFile(null)
                    setImageFileName("")
                    setImageCaption("")
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isUploadingBlockImage}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadBlockImage}
                  disabled={isUploadingBlockImage || !selectedImageFile || !imageFileName.trim()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                >
                  {isUploadingBlockImage ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Upload Image
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* AI Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <Sparkles className="mr-2" size={20} />
                  Ask AI Assistant
                </h2>
                <button
                  onClick={() => {
                    setShowAIModal(false)
                    setAiQuery("")
                    setCurrentAIBlock(null)
                    setSelectedImage(null)
                    setImagePreview(null)
                  }}
                  className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 flex-1 flex flex-col overflow-y-auto">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  You're asking about:{" "}
                  <span className="font-medium">{currentAIBlock?.type === "text" ? "Text" : "Diagram"} Block</span>
                </p>
                <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <code className="text-xs text-gray-700 font-mono">
                    {currentAIBlock?.content?.substring(0, 200)}
                    {currentAIBlock?.content?.length > 200 ? "..." : ""}
                  </code>
                </div>
              </div>
              {/* Image Upload Section for AI */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Image (Optional)</label>
                {!selectedImage ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      id="ai-image-upload"
                    />
                    <label htmlFor="ai-image-upload" className="cursor-pointer flex flex-col items-center">
                      <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">Click to upload an image</span>
                      <span className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</span>
                    </label>
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <img
                          src={imagePreview || "/placeholder.svg"}
                          alt="Selected image"
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{selectedImage.name}</p>
                        <p className="text-xs text-gray-500">{(selectedImage.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <button
                        onClick={removeSelectedImage}
                        className="flex-shrink-0 text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                        title="Remove image"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-1 flex flex-col">
                <label htmlFor="ai-query" className="block text-sm font-medium text-gray-700 mb-2">
                  What would you like me to help you with?
                </label>
                <textarea
                  id="ai-query"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  className="flex-1 min-h-[120px] border border-gray-300 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="For example: 'Add more explanation to this equation', 'Fix the formatting', 'Add examples', 'Improve this diagram', etc."
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAIModal(false)
                    setAiQuery("")
                    setCurrentAIBlock(null)
                    setSelectedImage(null)
                    setImagePreview(null)
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isAIProcessing || isUploadingImage}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAISend}
                  disabled={isAIProcessing || isUploadingImage || !aiQuery.trim()}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                >
                  {isUploadingImage ? (
                    <>
                      <Upload size={16} />
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </>
                  ) : isAIProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send to AI
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RenderingLatexAndPdfOfDigitisedNotes
